import React, { useEffect, useMemo, useState } from 'react';

import CombinedClientScopeFilters from './CombinedClientScopeFilters';
import useCombinedReportScope from './useCombinedReportScope';
import { fetchCombinedBalanceSheet, resolveCombinedReportError } from './combinedReportUtils';
import '../balance-sheet/BalanceSheet.css';
import '../consolidated-reports/ConsolidatedReports.css';

const apiUrl = process.env.REACT_APP_API_URL;

const formatAmount = (value) => `₹ ${Number(value ?? 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const CombinedBalanceSheet = () => {
  const {
    financialYear,
    clientOptions,
    clientsLoading,
    clientScopeMode,
    selectedClientIds,
    effectiveClientIds,
    scopeSummary,
    selectionPreview,
    onScopeModeChange,
    onToggleClientId,
  } = useCombinedReportScope();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!financialYear || effectiveClientIds.length === 0) {
      setData(null);
      setError('');
      setLoading(false);
      return;
    }

    let ignore = false;
    setLoading(true);

    fetchCombinedBalanceSheet(apiUrl, effectiveClientIds, financialYear, {
      allAccessibleClients: clientScopeMode === 'all',
    })
      .then((response) => {
        if (ignore) {
          return;
        }
        setData(response);
        setError('');
      })
      .catch((requestError) => {
        if (!ignore) {
          setError(resolveCombinedReportError(requestError, 'Unable to load the combined balance sheet.'));
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [clientScopeMode, effectiveClientIds, financialYear]);

  const liabilitySections = useMemo(() => Object.entries(data?.Liabilities || {}), [data]);
  const assetSections = useMemo(() => Object.entries(data?.Assets || {}), [data]);

  return (
    <div className="balance-sheet-page combined-report-page">
      <div className="combined-report-header">
        <p className="consolidated-reports-eyebrow">Combined Reports</p>
        <h2>Combined Balance Sheet</h2>
        <p className="financial-page-subtitle">Review one aggregated closing statement across all accessible clients or the selected subset for the current financial year.</p>
      </div>

      <CombinedClientScopeFilters
        clientOptions={clientOptions}
        clientsLoading={clientsLoading}
        clientScopeMode={clientScopeMode}
        selectedClientIds={selectedClientIds}
        effectiveClientIds={effectiveClientIds}
        financialYear={financialYear}
        onScopeModeChange={onScopeModeChange}
        onToggleClientId={onToggleClientId}
      />

      {!financialYear ? <p className="financial-message">Select a financial year from the header to load the combined report.</p> : null}
      {financialYear && clientOptions.length > 0 && effectiveClientIds.length === 0 ? <p className="financial-message">Choose one or more client IDs to load the combined report.</p> : null}
      {loading && <p className="financial-message">Loading combined balance sheet...</p>}
      {error && <p className="financial-message financial-message--error">{error}</p>}

      {data ? (
        <>
          <section className="consolidated-reports-stat-grid">
            <article className="consolidated-reports-stat-card">
              <span>Scope</span>
              <strong>{scopeSummary}</strong>
            </article>
            <article className="consolidated-reports-stat-card">
              <span>Total Assets</span>
              <strong>{formatAmount(data.totalAssets)}</strong>
            </article>
            <article className="consolidated-reports-stat-card">
              <span>Total Liabilities</span>
              <strong>{formatAmount(data.totalLiabilities)}</strong>
            </article>
            <article className="consolidated-reports-stat-card consolidated-reports-stat-card--accent">
              <span>Balance Check</span>
              <strong>{formatAmount(Number(data.totalAssets ?? 0) - Number(data.totalLiabilities ?? 0))}</strong>
            </article>
          </section>

          <p className="combined-report-caption">Included client IDs: {selectionPreview}</p>

          <div className="table-container">
            <table className="balance-sheet-table">
              <thead>
                <tr>
                  <th>Liabilities</th>
                  <th>Amount</th>
                  <th>Assets</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: Math.max(liabilitySections.length, assetSections.length) }).map((_, sectionIndex) => {
                  const [liabilitySectionLabel, liabilityRows] = liabilitySections[sectionIndex] || [];
                  const [assetSectionLabel, assetRows] = assetSections[sectionIndex] || [];
                  const liabilityEntries = Object.entries(liabilityRows || {});
                  const assetEntries = Object.entries(assetRows || {});
                  const rowCount = Math.max(liabilityEntries.length, assetEntries.length);

                  return (
                    <React.Fragment key={`section-${sectionIndex}`}>
                      <tr>
                        <td colSpan="2"><strong>{liabilitySectionLabel || ''}</strong></td>
                        <td colSpan="2"><strong>{assetSectionLabel || ''}</strong></td>
                      </tr>
                      {Array.from({ length: rowCount }).map((__, rowIndex) => {
                        const [liabilityLabel, liabilityAmount] = liabilityEntries[rowIndex] || [];
                        const [assetLabel, assetAmount] = assetEntries[rowIndex] || [];

                        return (
                          <tr key={`row-${sectionIndex}-${rowIndex}`}>
                            <td style={{ paddingLeft: '20px' }}>{liabilityLabel || ''}</td>
                            <td style={{ paddingLeft: '20px' }}>{liabilityLabel ? formatAmount(liabilityAmount) : ''}</td>
                            <td style={{ paddingLeft: '20px' }}>{assetLabel || ''}</td>
                            <td style={{ paddingLeft: '20px' }}>{assetLabel ? formatAmount(assetAmount) : ''}</td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                <tr>
                  <td>Total Liabilities</td>
                  <td>{formatAmount(data.totalLiabilities)}</td>
                  <td>Total Assets</td>
                  <td>{formatAmount(data.totalAssets)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default CombinedBalanceSheet;